/**
 * DocuSign Connect — settings panel.
 *
 * Renders inside Settings → Integrations. Two states:
 *   - Not configured: shows the webhook URL to paste into DocuSign
 *     Connect + a one-time secret entry form.
 *   - Configured: confirms it's wired up + offers Rotate / Disconnect.
 *
 * The secret itself is never returned by the GET endpoint (only a
 * boolean), so once configured the UI simply confirms it's set —
 * customers who lost their secret rotate by entering a new one.
 *
 * Spec: backend/docs/specs/ESIGN_TRACKING_SPEC.md
 */

import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

export default function DocuSignSettings({ authToken, isAdmin, onToast }) {
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_BASE}/api/integrations/docusign/status`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) throw new Error(`status ${r.status}`);
        const body = await r.json();
        if (!cancelled) {
          setConfigured(!!body.configured);
          setWebhookUrl(body.webhookUrl || '');
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authToken]);

  const saveSecret = async () => {
    if (!secret.trim()) {
      setError('Paste the DocuSign HMAC secret first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/integrations/docusign/secret`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ secret: secret.trim() }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Save failed (${r.status})`);
      setSecret('');
      setConfigured(true);
      setShowRotate(false);
      if (onToast) onToast('DocuSign secret saved. Webhooks will validate from now on.', 'success');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect DocuSign? Webhook events will stop being processed until you reconfigure the secret.')) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/integrations/docusign/secret`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Disconnect failed (${r.status})`);
      }
      setConfigured(false);
      setShowRotate(false);
      if (onToast) onToast('DocuSign disconnected.', 'success');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      if (onToast) onToast('Webhook URL copied.', 'success');
    } catch (_) {
      // Clipboard can fail on insecure contexts — fall back to manual select.
      if (onToast) onToast('Could not copy. Select the URL manually.', 'error');
    }
  };

  if (loading) {
    return <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Loading DocuSign status…</div>;
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <strong style={{ fontSize: 15 }}>DocuSign</strong>
        <span style={configured ? pillOnStyle : pillOffStyle}>
          {configured ? 'Connected' : 'Not connected'}
        </span>
      </div>
      <p style={{ margin: '0 0 12px', color: 'var(--room-text-body, #475569)', fontSize: 13, lineHeight: 1.5 }}>
        Track contract engagement (sent / viewed / signed) on the Buyer Space Documents tab.
        When an envelope completes, the Space auto-flips to Won and the Sales Handoff Brief
        auto-fires.
      </p>

      {error && <div style={errorStyle}>{error}</div>}

      {!configured && (
        <>
          <div style={subheadStyle}>1. Add a Connect listener in DocuSign</div>
          <p style={smallMuted}>
            DocuSign Admin → Connect → Add Configuration. Paste this URL, set authentication
            to HMAC, and copy the secret DocuSign generates.
          </p>
          <div style={urlRowStyle}>
            <code style={codeStyle}>{webhookUrl}</code>
            <button type="button" onClick={copyUrl} style={smallBtnStyle}>Copy</button>
          </div>

          <div style={{ ...subheadStyle, marginTop: 16 }}>2. Paste the HMAC secret here</div>
          <p style={smallMuted}>
            Stored encrypted in our database. Used to verify every webhook came from DocuSign
            (not a spoofed request). Never returned from our API after you save it.
          </p>
          {!isAdmin && (
            <div style={{ ...errorStyle, background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}>
              Only company admins can configure integrations.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              type="password"
              placeholder="DocuSign HMAC secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              disabled={busy || !isAdmin}
              style={inputStyle}
            />
            <button
              type="button"
              onClick={saveSecret}
              disabled={busy || !isAdmin || !secret.trim()}
              style={primaryBtnStyle(busy || !isAdmin || !secret.trim())}
            >
              {busy ? 'Saving…' : 'Connect'}
            </button>
          </div>
        </>
      )}

      {configured && !showRotate && (
        <>
          <p style={{ margin: '8px 0 12px', color: 'var(--room-text-strong, #1e293b)', fontSize: 13 }}>
            DocuSign is connected. Webhooks at <code style={codeInlineStyle}>{webhookUrl}</code>
            are validated against your saved HMAC secret.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowRotate(true)}
              disabled={busy || !isAdmin}
              style={secondaryBtnStyle(busy || !isAdmin)}
            >
              Rotate secret
            </button>
            <button
              type="button"
              onClick={disconnect}
              disabled={busy || !isAdmin}
              style={dangerBtnStyle(busy || !isAdmin)}
            >
              Disconnect
            </button>
          </div>
          {!isAdmin && <div style={smallMuted}>Only admins can rotate or disconnect.</div>}
        </>
      )}

      {configured && showRotate && (
        <>
          <p style={smallMuted}>
            Generate a new secret in DocuSign Connect, then paste it here. The old secret stops
            validating immediately, so make sure DocuSign is using the new one.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="password"
              placeholder="New HMAC secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              disabled={busy}
              style={inputStyle}
            />
            <button
              type="button"
              onClick={saveSecret}
              disabled={busy || !secret.trim()}
              style={primaryBtnStyle(busy || !secret.trim())}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowRotate(false); setSecret(''); }}
              disabled={busy}
              style={secondaryBtnStyle(busy)}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────

const cardStyle = {
  background: 'var(--room-card, #ffffff)',
  border: '1px solid var(--room-border, #e2e8f0)',
  borderRadius: 12,
  padding: 16,
  marginTop: 12,
};
const subheadStyle = {
  fontSize: 12, fontWeight: 600, color: 'var(--room-text-body, #475569)',
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, marginTop: 8,
};
const smallMuted = { margin: '0 0 6px', color: 'var(--room-text-muted, #64748b)', fontSize: 12.5, lineHeight: 1.5 };
const urlRowStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: 'var(--room-surface, #f8fafc)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 8,
  padding: '8px 10px',
};
const codeStyle = {
  flex: 1, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
  color: 'var(--room-text-strong, #1e293b)', wordBreak: 'break-all',
};
const codeInlineStyle = {
  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
  background: 'var(--room-border-soft, #f1f5f9)', padding: '1px 6px', borderRadius: 4,
};
const inputStyle = {
  flex: 1, padding: '8px 12px', borderRadius: 8,
  border: '1px solid #cbd5e1', fontSize: 13, fontFamily: 'inherit',
};
const errorStyle = {
  marginBottom: 10, padding: '8px 12px', borderRadius: 8,
  background: '#fee2e2', border: '1px solid #fecaca',
  color: '#991b1b', fontSize: 13,
};
const pillOnStyle = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
  background: '#dcfce7', color: '#166534',
};
const pillOffStyle = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
  background: 'var(--room-border-soft, #f1f5f9)', color: 'var(--room-text-muted, #64748b)',
};
function primaryBtnStyle(disabled) {
  return {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
    fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
  };
}
function secondaryBtnStyle(disabled) {
  return {
    padding: '8px 14px', borderRadius: 8,
    border: '1px solid #c4b5fd', background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
  };
}
function dangerBtnStyle(disabled) {
  return {
    padding: '8px 14px', borderRadius: 8,
    border: '1px solid #fecaca', background: 'var(--room-card, #fff)', color: '#b91c1c',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
  };
}
const smallBtnStyle = {
  padding: '6px 10px', borderRadius: 6,
  border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
  fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
};
